import { runInAction, makeAutoObservable } from 'mobx';
import { get, post, requests, patch } from '@partsbadger/utils';

import { CoatingsProps } from '@partsbadger/types';
import { QuoteLineItemProps } from '../types/types';

class CoatingStore {
    coatings: Array<CoatingsProps> = [];
    topCoatings: Array<CoatingsProps> = [];
    coatingsByMaterial = new Map<number, CoatingsProps[]>();
    fetchedMaterialIds = new Set<number>();
    private inFlightByMaterialId = new Map<number, Promise<CoatingsProps[]>>();

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    getCoatings() {
        get(`/customer/coatings/`)
            .then(data => {
                runInAction(() => {
                    this.coatings = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    getTopCoatings() {
        get(`/customer/coatings/top-coatings`)
            .then(data => {
                runInAction(() => {
                    this.topCoatings = data;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    async getCoatingsByMaterial(materialId: number) {
        if (this.coatingsByMaterial.has(materialId)) return this.coatingsByMaterial.get(materialId);

        const existing = this.inFlightByMaterialId.get(materialId);
        if (existing) return existing;

        const req = get(`/customer/coatings/by_material/?material_id=${materialId}`)
            .then(data => {
                const results: CoatingsProps[] = data?.results || [];
                runInAction(() => {
                    this.coatingsByMaterial.set(materialId, results);
                    this.fetchedMaterialIds.add(materialId);
                });
                return results;
            })
            .catch(error => {
                // If the request fails, allow future retries.
                console.error(error);
                runInAction(() => {
                    this.fetchedMaterialIds.delete(materialId);
                });
                return [];
            })
            .finally(() => {
                runInAction(() => {
                    this.inFlightByMaterialId.delete(materialId);
                });
            });

        runInAction(() => {
            // Mark as in-flight immediately so concurrent calls dedupe.
            this.inFlightByMaterialId.set(materialId, req);
        });

        return req;
    }

    async syncCoatingsForQuote(line_items?: QuoteLineItemProps[]) {
        if (!line_items?.length) return;

        const materialIds = Array.from(
            new Set(
                line_items?.map(item => item.product?.material_related?.id).filter((id): id is number => Boolean(id))
            )
        );

        const missingIds = materialIds.filter(
            id =>
                !this.coatingsByMaterial.has(id) &&
                !this.inFlightByMaterialId.has(id) &&
                !this.fetchedMaterialIds.has(id)
        );

        if (!missingIds.length) return;

        await Promise.all(missingIds.map(id => this.getCoatingsByMaterial(id)));
    }
}

const coatingStore = new CoatingStore();

export default coatingStore;
